import { FRUIT_PRODUCTS, OFFICIAL_FRUIT_RIGHT_PAIRS } from "./data/products";
import {
  DEFAULT_ROUND_COUNT,
  FRUIT_PRODUCT_IDS,
  type FruitMarketByProduct,
  type FruitProductId,
  type FruitShopV2Phase,
  type FruitShopV2Round,
  type FruitShopV2State,
  type InitGameOptions,
  OFFICIAL_PLAYER_COUNT,
} from "./types";

function getDefaultSeed(playerIds: readonly string[]): number {
  const source = playerIds.join("|");
  let hash = 2166136261;
  for (let i = 0; i < source.length; i += 1) {
    hash ^= source.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function createRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(values: readonly T[], seed: number): readonly T[] {
  const next = [...values];
  const random = createRandom(seed);
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
}

function assertValidPlayerIds(
  playerIds: readonly string[],
  hostPlayerId: string,
): void {
  if (playerIds.length !== OFFICIAL_PLAYER_COUNT) {
    throw new Error(
      `Fruit Shop v2 official mode requires ${OFFICIAL_PLAYER_COUNT} players`,
    );
  }

  const deduped = new Set(playerIds);
  if (deduped.size !== playerIds.length) {
    throw new Error("Player IDs must be unique");
  }

  if (!deduped.has(hostPlayerId)) {
    throw new Error("Host player must be included in player IDs");
  }
}

export function dealFruitRights(
  playerIds: readonly string[],
  seed: number,
): Readonly<Record<string, readonly FruitProductId[]>> {
  const shuffledPairs = shuffle(OFFICIAL_FRUIT_RIGHT_PAIRS, seed);

  return Object.fromEntries(
    playerIds.map((playerId, index) => [playerId, shuffledPairs[index]]),
  );
}

export function buildMarket(
  players: readonly {
    readonly fruitRights: readonly FruitProductId[];
  }[],
): FruitMarketByProduct {
  return Object.fromEntries(
    FRUIT_PRODUCTS.map((product) => [
      product.productId,
      {
        productId: product.productId,
        sellerCount: players.filter((player) =>
          player.fruitRights.includes(product.productId),
        ).length,
      },
    ]),
  ) as FruitMarketByProduct;
}

export function createRound(
  players: readonly {
    readonly fruitRights: readonly FruitProductId[];
  }[],
  roundNumber: number,
  phase: FruitShopV2Phase = "ITEM_WINDOW",
): FruitShopV2Round {
  return {
    roundNumber,
    phase,
    itemReadyPlayerIds: [],
    priceSubmissions: {},
    secretSelections: {},
    changeRecords: [],
    market: buildMarket(players),
    productResults: [],
    incomeByPlayerId: {},
    confirmedPlayerIds: [],
  };
}

export function isKnownFruitProductId(
  productId: string,
): productId is FruitProductId {
  return FRUIT_PRODUCT_IDS.includes(productId as FruitProductId);
}

export function initGame(options: InitGameOptions): FruitShopV2State {
  assertValidPlayerIds(options.playerIds, options.hostPlayerId);
  const seed = options.seed ?? getDefaultSeed(options.playerIds);
  const dealtRights = dealFruitRights(options.playerIds, seed);
  const players = options.playerIds.map((playerId) => ({
    playerId,
    fruitRights: dealtRights[playerId] ?? [],
    totalIncomeWon: 0,
    itemStatus: "AVAILABLE" as const,
  }));

  return {
    gameId: options.gameId ?? `fruit-shop-v2-${options.playerIds.join("-")}`,
    status: "IN_PROGRESS",
    roundCount: options.roundCount ?? DEFAULT_ROUND_COUNT,
    currentRound: createRound(players, 1),
    completedRounds: [],
    players,
    winnerIds: [],
    lowestPlayerIds: [],
    garnetAwardsByPlayerId: {},
    lifeTokenAwardsByPlayerId: {},
    extensionNumber: 0,
    hostPlayerId: options.hostPlayerId,
    seed,
  };
}
